/**
 * Strix Healthcare — App Router
 *
 * Uses wouter for client-side routing with lazy-loaded pages.
 *
 * This portal serves Strix Gov RCM: sign in, the Recovery screen and the public proof pages. Any
 * other path goes to Recovery or sign in. The earlier clinical portal is parked behind
 * `VITE_LEGACY_PORTAL_ENABLED` (lib/portal-scope.ts): its pages stay in the tree, and none of its
 * routes exist unless the build turns it on.
 */

import { Suspense, lazy } from 'react';
import { Router, Route, Switch } from 'wouter';
import { useAuth } from './contexts/AuthContext';
import { useUser } from './contexts/AuthContext';
import LoadingScreen from './components/common/LoadingScreen';
import AuthGuard from './components/common/AuthGuard';
import Layout from './components/common/Layout';
import { LEGACY_PORTAL_ENABLED, homePathForRole } from './lib/portal-scope';

const SignInPage = lazy(() => import('./pages/auth/SignIn'));
const SignUpPage = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/auth/SignUp')) : Parked;
const PatientDashboard = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/patient/PatientDashboard')) : Parked;
const AppointmentsPage = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/patient/AppointmentsPage')) : Parked;
const AppointmentDetail = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/patient/AppointmentDetail')) : Parked;
const MedicalRecordsPage = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/patient/MedicalRecordsPage')) : Parked;
const RecordDetail = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/patient/RecordDetail')) : Parked;
const PrescriptionsPage = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/patient/PrescriptionsPage')) : Parked;
const TelehealthPage = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/patient/TelehealthPage')) : Parked;
const TelehealthSession = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/patient/TelehealthSession')) : Parked;
const MessagesPage = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/patient/Messages')) : Parked;
const ProfilePage = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/patient/ProfilePage')) : Parked;
const ProviderDashboard = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/provider/ProviderDashboard')) : Parked;
const PatientList = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/provider/PatientListPage')) : Parked;
const PatientDetail = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/provider/PatientDetailPage')) : Parked;
const ProviderSchedule = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/provider/SchedulePage')) : Parked;
const AdminDashboard = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/admin/AdminDashboard')) : Parked;
const GovernanceDashboard = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/admin/GovernanceDashboard')) : Parked;
const StrixConsole = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/admin/StrixConsole')) : Parked;
const ProofTrustCenter = lazy(() => import('./pages/proof/ProofTrustCenter'));
const NonMemberDashboard = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/patient/NonMemberDashboard')) : Parked;
const LandingPage = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/public/LandingPage')) : Parked;
const UMQueue = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/um/UMQueue')) : Parked;
const UMCaseReview = LEGACY_PORTAL_ENABLED ? lazy(() => import('./pages/um/UMCaseReview')) : Parked;
const Recovery = lazy(() => import('./pages/strix-gov-rcm/Recovery'));

/**
 * Stands in for a parked page. Written as `LEGACY_PORTAL_ENABLED ? lazy(...) : Parked` at each
 * parked page so that a build without the clinical portal does not emit the page's code at all:
 * the constant condition lets the bundler drop the dynamic import.
 */
function Parked() {
  return null;
}

function ProtectedRoute({
  component: Component,
  requiredRole,
}: {
  component: React.ComponentType;
  requiredRole?: string;
}) {
  return (
    <AuthGuard requiredRole={requiredRole}>
      <Layout>
        <Suspense fallback={<LoadingScreen />}>
          <Component />
        </Suspense>
      </Layout>
    </AuthGuard>
  );
}

/**
 * A signed-in page that draws its own chrome. The Recovery screen is the recovery workspace, with
 * its own sidebar and header, so it is not wrapped in the portal Layout.
 */
function WorkspaceRoute({
  component: Component,
  requiredRole,
}: {
  component: React.ComponentType;
  requiredRole?: string;
}) {
  return (
    <AuthGuard requiredRole={requiredRole}>
      <Suspense fallback={<LoadingScreen />}>
        <Component />
      </Suspense>
    </AuthGuard>
  );
}

function PublicRoute({ component: Component }: { component: React.ComponentType }) {
  const { isSignedIn } = useAuth();
  const { user } = useUser();

  if (isSignedIn) {
    const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
    window.location.href = homePathForRole(role);
    return null;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Component />
    </Suspense>
  );
}

/**
 * Role-aware dashboard — shows NonMemberDashboard for non_member users,
 * PatientDashboard for everyone else with sufficient access.
 */
function DashboardRoute() {
  const { user } = useAuth();
  const role = user?.role || 'non_member';

  if (role === 'non_member') {
    return (
      <AuthGuard>
        <Layout>
          <Suspense fallback={<LoadingScreen />}>
            <NonMemberDashboard />
          </Suspense>
        </Layout>
      </AuthGuard>
    );
  }

  return <ProtectedRoute component={PatientDashboard} requiredRole="patient" />;
}

export default function App() {
  return (
    <Router>
      <Switch>
      <Route path="/">
        {() => (LEGACY_PORTAL_ENABLED ? (
          <Suspense fallback={<LoadingScreen />}>
            <LandingPage />
          </Suspense>
        ) : <HomeRedirect />)}
      </Route>

      <Route path="/sign-in" component={() => <PublicRoute component={SignInPage} />} />

      {/* Governed revenue recovery: the operator's screen. */}
      <Route
        path="/admin/recovery"
        component={() => <WorkspaceRoute component={Recovery} requiredRole="admin" />}
      />

      {/* Public proof pages — no auth required, but show in Layout when signed in */}
      <Route path="/proof-trust-center">
        {() => (
          <Suspense fallback={<LoadingScreen />}>
            <ProofTrustCenter />
          </Suspense>
        )}
      </Route>
      <Route path="/proof/:evidenceId">
        {() => (
          <Suspense fallback={<LoadingScreen />}>
            <ProofTrustCenter />
          </Suspense>
        )}
      </Route>
      <Route path="/proof">
        {() => (
          <Suspense fallback={<LoadingScreen />}>
            <ProofTrustCenter />
          </Suspense>
        )}
      </Route>

      <Route path="/not-available" component={() => <NotAvailable />} />

      {/* The parked clinical portal. None of these routes exist unless the build turns it on. */}
      {LEGACY_PORTAL_ENABLED && legacyRoutes()}

      <Route component={() => <HomeRedirect />} />
      </Switch>
    </Router>
  );
}

/** Everything unknown, and `/`: to the Recovery screen when an operator is signed in, else to sign in. */
function HomeRedirect() {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  if (!isLoaded) return <LoadingScreen />;
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  window.location.replace(isSignedIn ? homePathForRole(role) : '/sign-in');
  return null;
}

/** A signed-in person without an operator role: this portal has nothing for them. */
function NotAvailable() {
  const { signOut } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-gray-900">This portal is for Strix Gov RCM operators</h1>
        <p className="mt-2 text-sm text-gray-600">Your account does not have an operator role. Ask an administrator for access.</p>
        <button type="button" onClick={() => signOut()} className="mt-5 rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white">Sign out</button>
      </div>
    </div>
  );
}

/** The parked clinical portal's routes. Rendered only when `VITE_LEGACY_PORTAL_ENABLED=true`. */
function legacyRoutes() {
  return (
    <>
      <Route path="/sign-up" component={() => <PublicRoute component={SignUpPage} />} />
      <Route path="/dashboard" component={() => <DashboardRoute />} />
      <Route
        path="/appointments"
        component={() => <ProtectedRoute component={AppointmentsPage} requiredRole="patient" />}
      />
      <Route
        path="/appointments/:id"
        component={() => <ProtectedRoute component={AppointmentDetail} requiredRole="patient" />}
      />
      <Route
        path="/records"
        component={() => <ProtectedRoute component={MedicalRecordsPage} requiredRole="patient" />}
      />
      <Route
        path="/records/:id"
        component={() => <ProtectedRoute component={RecordDetail} requiredRole="patient" />}
      />
      <Route
        path="/prescriptions"
        component={() => <ProtectedRoute component={PrescriptionsPage} requiredRole="patient" />}
      />
      <Route path="/telehealth" component={() => <ProtectedRoute component={TelehealthPage} requiredRole="patient" />} />
      <Route
        path="/telehealth/:sessionId"
        component={() => <ProtectedRoute component={TelehealthSession} requiredRole="patient" />}
      />
      <Route path="/messages" component={() => <ProtectedRoute component={MessagesPage} requiredRole="patient" />} />
      <Route path="/profile" component={() => <ProtectedRoute component={ProfilePage} requiredRole="patient" />} />

      <Route
        path="/provider"
        component={() => <ProtectedRoute component={ProviderDashboard} requiredRole="provider" />}
      />
      <Route
        path="/provider/patients"
        component={() => <ProtectedRoute component={PatientList} requiredRole="provider" />}
      />
      <Route
        path="/provider/patients/:id"
        component={() => <ProtectedRoute component={PatientDetail} requiredRole="provider" />}
      />
      <Route
        path="/provider/schedule"
        component={() => <ProtectedRoute component={ProviderSchedule} requiredRole="provider" />}
      />

      <Route
        path="/admin"
        component={() => <ProtectedRoute component={AdminDashboard} requiredRole="admin" />}
      />
      <Route
        path="/admin/governance"
        component={() => <ProtectedRoute component={GovernanceDashboard} requiredRole="admin" />}
      />
            <Route path="/admin/console"
        component={() => <ProtectedRoute component={StrixConsole} requiredRole="admin" />}
      />

      {/* Utilization Management — governance-protected workflow */}
      <Route
        path="/um"
        component={() => <ProtectedRoute component={UMQueue} requiredRole="admin" />}
      />
      <Route
        path="/um/:caseId"
        component={() => <ProtectedRoute component={UMCaseReview} requiredRole="admin" />}
      />

    </>
  );
}
